// @flow strict

import * as React from 'react'
import cx from 'classnames'
import { prettyPrintPriceFromMinor } from '@mondough/public-web-components/src/utils/currencyUtils'

import { SectionThemes, SectionWrapper } from '../../../SectionWrapper'

import {
  BusinessButton,
  AccessibilityText,
  Tooltip,
} from '@mondough/public-web-components'
import { type PricingTableProps, CheckMark, Cross } from './PricingTable'

import styles from './DesktopPricingTable.module.scss'

const PricingTable = ({
  featureGroups,
  plans,
  title,
  signupLink,
}: PricingTableProps) => {
  return (
    <SectionWrapper
      theme={SectionThemes.LIGHT}
      className={styles.section}
      contentClassName={styles.baseContent}
    >
      <h2 className={styles.title}>{title}</h2>
      <table
        className={styles.pricingTable}
        aria-label="Features available for Pro and Lite tiers"
      >
        <tbody>
          <tr className={styles.planTypesRow}>
            <th scope="col" className={styles.firstFeatureGroupHeader}>
              {featureGroups[0].title}
            </th>
            {plans.map((plan, i) => (
              <th
                className={cx(
                  styles.cell,
                  styles.featureValue,
                  styles.planHeader,
                  {
                    [styles.greyCell]: plan.id === 'PRO',
                  },
                )}
                key={plan.id}
                scope="col"
              >
                <div className={styles.planTitlePrice}>
                  <span className={styles.planTitle}>{plan.name}</span>
                  <span className={styles.planPrice}>
                    {plan.pricePerMonthInPence === 0 ? (
                      'Free'
                    ) : (
                      <>
                        £{prettyPrintPriceFromMinor(plan.pricePerMonthInPence)}
                        <span className={styles.priceCadence}>/m</span>
                      </>
                    )}
                  </span>
                </div>

                <span className={styles.planTagline}>{plan.tagline}</span>
                <div className={styles.planCta}>
                  <BusinessButton
                    theme={plan.id === 'PRO' ? 'primary' : 'secondary'}
                    href={signupLink}
                    className={cx({
                      [styles.secondaryCta]: plan.id === 'LITE',
                    })}
                  >
                    Get Business {plan.name}
                  </BusinessButton>
                </div>
              </th>
            ))}
          </tr>
          {featureGroups.map(
            ({ features, title: groupTitle }, featureGroupIndex) => (
              <>
                {featureGroupIndex > 0 && (
                  <div className={styles.featureGroupTitle}>{groupTitle}</div>
                )}
                {features.map((feature, featureIndex) => {
                  const featureKey = feature.title.replace(' ', '')
                  return (
                    <tr
                      key={featureKey}
                      className={cx({
                        // we add rounded corners depending on what index the value is
                        // this wouldn't work with css pseudoelements as we have the feature groups
                        [styles.roundedTopRow]: featureIndex === 0,
                        [styles.roundedBottomRow]:
                          featureIndex === features.length - 1,
                      })}
                    >
                      <th
                        className={cx(styles.cell, styles.featureTitle)}
                        scope="row"
                      >
                        <div className={styles.featureTitleWrapper}>
                          <img
                            className={styles.icon}
                            src={feature.icon}
                            alt=""
                          />
                          {feature.title}
                          <Tooltip
                            className={styles.tooltipIcon}
                            id={`${featureKey}-tooltip`}
                            label="More information"
                            description={feature.description}
                            flipped={
                              // don't let it get cut off at the bottom.
                              featureGroupIndex === featureGroups.length - 1 &&
                              featureIndex >= features.length - 2
                            }
                            link={
                              feature.tooltipLink != null
                                ? feature.tooltipLink
                                : undefined
                            }
                            linkText={feature.tooltipLinkText || ''}
                          />
                        </div>
                      </th>
                      {plans.map(plan => (
                        <td
                          className={cx(styles.cell, styles.featureValue, {
                            [styles.greyCell]: plan.id === 'PRO',
                          })}
                          key={`${featureKey}_${plan.id}`}
                        >
                          {feature.plans.includes(plan.id) ? (
                            <>
                              <CheckMark />
                              <AccessibilityText>Yes</AccessibilityText>
                            </>
                          ) : (
                            <>
                              <Cross />
                              <AccessibilityText>No</AccessibilityText>
                            </>
                          )}
                        </td>
                      ))}
                    </tr>
                  )
                })}
              </>
            ),
          )}
        </tbody>
      </table>
    </SectionWrapper>
  )
}

export default PricingTable
